import type { LanguageModelUsage } from 'ai';
import { events } from '../helpers/static-data';
import type { WrappedTimestamp } from '../providers/client-provider';
import type { ID, Model } from './models';
import type { Photo } from './photo';
import type { TrainingEffect, WorkoutSet, WorkoutUpdateReason } from './training';
import type { DayOfWeek } from './training-plan';
import type { RunResult } from './user';

export enum EventOwner {
  System = 'system',
  User = 'user',
  Coach = 'coach',
}

export type DistanceType = (typeof events)[number]['value'];
export enum EventType {
  RACE = 'race',
  TRAINING = 'training',
}
export enum EventResult {
  FINISHED = 'finished',
  DNF = 'dnf',
  DNS = 'dns',
  CANCELLED = 'cancelled',
  CANCELLED_PERSONALLY = 'cancelled_personally',
}

export interface EventMetadata extends Record<string, string | null | undefined> {
  equipmentRequirements?: string | null;
  officialDescription?: string | null;
  startLocation?: string | null;
  emergencyHotline?: string | null;
  essentialInfo?: string | null;
  summary?: string | null;
  raceDayInfo?: string | null;
  routeInfo?: string | null;
  raceDocumentCollectionInfo?: string | null;
}

export interface TrainingPlanSettingsPayload {
  workoutPerWeek: number;
  hardWorkoutPerWeek: number;
  longRunPerWeek: number;
  workoutDays: DayOfWeek[];
  longRunDays: DayOfWeek[];
}

export interface EventCommon {
  userId: ID;
  owner: EventOwner;
  type: EventType;

  startAt: WrappedTimestamp;
  endAt: WrappedTimestamp;
  dateStr: string;

  name: string;
  description: string;

  metadata?: EventMetadata;
}

export interface GlobalRaceEvent extends EventCommon, Model {
  type: EventType.RACE;
  distance: DistanceType;
  distanceInMeters?: number;
  urls?: string[];

  files?: Photo[];
}

export interface PersonalEventCommon extends EventCommon {
  activityId?: ID;
  activityTopicId?: ID;

  topicId?: ID;
}

export interface RaceEvent extends GlobalRaceEvent, PersonalEventCommon, Model {
  type: EventType.RACE;
  target?: RunResult | null;

  result?: EventResult;
  report?: string;

  usage?: LanguageModelUsage;

  pending?: boolean;
  isMainEvent?: boolean;
  trainingPlanSettings?: TrainingPlanSettingsPayload | null;

  commonRaceId?: ID | null;
}

export interface TrainingEvent extends PersonalEventCommon, Model {
  type: EventType.TRAINING;
  plannedTrainingEffect: TrainingEffect;
  actualTrainingEffect?: TrainingEffect;

  plannedSections?: WorkoutSet[];
  updateReason?: WorkoutUpdateReason;
  updateSummary?: string;

  trainingPlanId?: ID | null;
}

export type Event = RaceEvent | TrainingEvent;

export type EventWithDate = Event & {
  date: Date;
  big: boolean;
};

export interface SaveEventPayload {
  type: EventType;
  name: string;
  description?: string;
  startAt: Date;
  isMainEvent: boolean;
  distance: DistanceType | null;
  distanceInMeters: number | null;
  target: RunResult | null;
  urls: string[];
  result: EventResult | null;
  report: string | null;
  activityId: string | null;
  metadata: EventMetadata | null;
  plannedTrainingEffect: TrainingEffect | null;
  plannedSections: WorkoutSet[] | null;
  files: Photo[];
  trainingPlanSettings?: TrainingPlanSettingsPayload | null;
  commonRaceId?: ID | null;
}

export type AddEventFromUrlPayload = Partial<Event> & {
  isMainEvent?: boolean;
  trainingPlanSettings?: TrainingPlanSettingsPayload | null;
};
